import {
  Array,
  Boolean,
  Dictionary,
  Literal,
  Null,
  Number,
  Optional,
  Record as RRecord,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums";

// Model definitions
export const SnowflakeCortexModelLiteral = Union(
  Literal("SNOWFLAKE_CORTEX_CLAUDE_3_5_SONNET"),
  Literal("SNOWFLAKE_CORTEX_CLAUDE_4_SONNET"),
  Literal("SNOWFLAKE_CORTEX_CLAUDE_4_5_SONNET"),
);
export type SnowflakeCortexModel = Static<typeof SnowflakeCortexModelLiteral>;
export const SnowflakeCortexModel = getNormalEnum(SnowflakeCortexModelLiteral);

export const SnowflakeCortexModelNameMap: Record<SnowflakeCortexModel, string> =
  {
    [SnowflakeCortexModel.SNOWFLAKE_CORTEX_CLAUDE_3_5_SONNET]:
      "claude-3-5-sonnet",
    [SnowflakeCortexModel.SNOWFLAKE_CORTEX_CLAUDE_4_SONNET]: "claude-4-sonnet",
    [SnowflakeCortexModel.SNOWFLAKE_CORTEX_CLAUDE_4_5_SONNET]:
      "claude-sonnet-4-5",
  };

export const SnowflakeCortexMessageRoleLiteral = Union(
  Literal("user"),
  Literal("assistant"),
  Literal("system"),
);
export type SnowflakeCortexMessageRole = Static<
  typeof SnowflakeCortexMessageRoleLiteral
>;
export const SnowflakeCortexMessageRole = getNormalEnum(
  SnowflakeCortexMessageRoleLiteral,
);

export const SnowflakeCortexToolUseBlock = RRecord({
  type: Literal("tool_use"),
  tool_use: RRecord({
    tool_use_id: String,
    name: String,
    input: Union(String, Dictionary(Unknown, String)),
  }),
});
export type SnowflakeCortexToolUseBlock = Static<
  typeof SnowflakeCortexToolUseBlock
>;

export const SnowflakeCortexTextBlock = RRecord({
  type: Literal("text"),
  text: String,
});
export type SnowflakeCortexTextBlock = Static<typeof SnowflakeCortexTextBlock>;
export const SnowflakeCortexToolResultBlock = RRecord({
  type: Literal("tool_results"),
  tool_results: RRecord({
    tool_use_id: String,
    name: String,
    content: Array(SnowflakeCortexTextBlock),
  }),
});
export type SnowflakeCortexToolResultBlock = Static<
  typeof SnowflakeCortexToolResultBlock
>;

// Content block types for response processing

export const SnowflakeCortexThinkingBlock = RRecord({
  type: Literal("anthropic"),
  anthropic: RRecord({
    thinking: RRecord({
      thinking: String,
      signature: Optional(String), // Optional signature field for thinking blocks
    }),
  }),
});
export type SnowflakeCortexThinkingBlock = Static<
  typeof SnowflakeCortexThinkingBlock
>;

export const SnowflakeCortexMessage = Union(
  RRecord({
    role: Literal(SnowflakeCortexMessageRole.system),
    content: String,
  }),
  RRecord({
    role: Literal(SnowflakeCortexMessageRole.user),
    content: String,
    content_list: Optional(
      Array(Union(SnowflakeCortexToolUseBlock, SnowflakeCortexToolResultBlock)),
    ),
  }),
  RRecord({
    role: Literal(SnowflakeCortexMessageRole.assistant),
    content: String,
    content_list: Optional(
      Array(
        Union(
          SnowflakeCortexToolUseBlock,
          SnowflakeCortexToolResultBlock,
          SnowflakeCortexThinkingBlock,
          SnowflakeCortexTextBlock,
        ),
      ),
    ),
  }),
);
export type SnowflakeCortexMessage = Static<typeof SnowflakeCortexMessage>;

export const SnowflakeCortexToolType = Literal("generic");

export const SnowflakeCortexToolSpec = RRecord({
  type: String, // The type of tool. A combination of type and name is a unique identifier.
  name: String, // The name of the tool. A combination of type and name is a unique identifier.
  description: String, // A description of the tool being considered for tool use.
  input_schema: RRecord({
    type: SnowflakeCortexToolType, // The type of the input schema object. We only use `generic`
    properties: Dictionary(Unknown), // The definitions for each input parameter.
    required: Optional(Array(String)), // Array of required properties.
  }),
});
export type SnowflakeCortexToolSpec = Static<typeof SnowflakeCortexToolSpec>;

export const SnowflakeCortexToolResponse = RRecord({
  tool_use_id: Optional(String),
  name: Optional(String),
  input: Optional(Dictionary(Unknown, String)),
});
export type SnowflakeCortexToolResponse = Static<
  typeof SnowflakeCortexToolResponse
>;

export const SnowflakeCortexThinkingConfig = RRecord({
  enabled: Boolean,
  budget: Number.optional(),
});
export type SnowflakeCortexThinkingConfig = Static<
  typeof SnowflakeCortexThinkingConfig
>;

export const SnowflakeCortexContextEditingConfig = RRecord({
  enabled: Boolean,
  trigger: RRecord({
    type: String,
    value: Number,
  }).optional(),
  keep: RRecord({
    type: String,
    value: Number,
  }).optional(),
  clearAtLeast: RRecord({
    type: String,
    value: Number,
  }).optional(),
  excludeTools: Array(String).optional(),
  clearToolInputs: Boolean.optional(),
});
export type SnowflakeCortexContextEditingConfig = Static<
  typeof SnowflakeCortexContextEditingConfig
>;

export const SnowflakeCortexModelParams = RRecord({
  model: SnowflakeCortexModelLiteral,
  maxTokens: Number,
  temperature: Number.optional(),
  topP: Number.optional(),
  guardrails: Boolean.optional(),
  thinking: SnowflakeCortexThinkingConfig.optional(),
});
export type SnowflakeCortexModelParams = Static<
  typeof SnowflakeCortexModelParams
>;

// Response types
// Additional content block types for streaming responses
export const SnowflakeCortexToolContentBlock = RRecord({
  name: String,
  tool_use_id: String,
});
export type SnowflakeCortexToolContentBlock = Static<
  typeof SnowflakeCortexToolContentBlock
>;

export const SnowflakeCortexInputBlock = RRecord({
  input: String,
});
export type SnowflakeCortexInputBlock = Static<
  typeof SnowflakeCortexInputBlock
>;

// The content_list can contain any of these five kinds of blocks.
export const SnowflakeCortexContentBlock = Union(
  SnowflakeCortexTextBlock,
  SnowflakeCortexToolContentBlock,
  SnowflakeCortexInputBlock,
  SnowflakeCortexToolUseBlock,
  SnowflakeCortexThinkingBlock,
);
export type SnowflakeCortexContentBlock = Static<
  typeof SnowflakeCortexContentBlock
>;
// The delta object may have an optional 'content' string and/or a 'content_list'
const SnowflakeCortexDelta = RRecord({
  content: Optional(String),
  content_list: Optional(
    Union(Array(SnowflakeCortexContentBlock), SnowflakeCortexContentBlock),
  ),
  // Tool-related fields
  type: Optional(String),
  name: Optional(String),
  tool_use_id: Optional(String),
  input: Optional(Union(String, Dictionary(Unknown, String))),
});
export type SnowflakeCortexDelta = Static<typeof SnowflakeCortexDelta>;
// Each choice contains a delta.
const SnowflakeCortexChoice = RRecord({
  delta: SnowflakeCortexDelta,
  finish_reason: Optional(String),
});

// Message structure for sync responses (non-streaming).
const SnowflakeCortexSyncMessage = RRecord({
  content: Optional(String),
  content_list: Optional(
    Union(Array(SnowflakeCortexContentBlock), SnowflakeCortexContentBlock),
  ),
});

// Choice structure for sync responses (non-streaming).
const SnowflakeCortexSyncChoice = RRecord({
  message: SnowflakeCortexSyncMessage,
  finish_reason: Optional(String),
});

// The usage object may be empty or have token counts.
const SnowflakeCortexUsage = RRecord({
  prompt_tokens: Optional(Number),
  completion_tokens: Optional(Number),
  total_tokens: Optional(Number),
});

// Top-level JSON object for streaming responses.
export const ResponseData = RRecord({
  id: String,
  model: String,
  choices: Array(SnowflakeCortexChoice),
  usage: SnowflakeCortexUsage,
});
export type ResponseData = Static<typeof ResponseData>;

// Top-level JSON object for sync (non-streaming) responses.
export const SyncResponseData = RRecord({
  choices: Array(SnowflakeCortexSyncChoice),
  usage: SnowflakeCortexUsage,
});
export type SyncResponseData = Static<typeof SyncResponseData>;

// Union type for both streaming and sync responses.
export const SnowflakeCortexResponse = Union(
  Array(ResponseData), // Streaming: array of ResponseData
  SyncResponseData, // Sync: single SyncResponseData
);
export type SnowflakeCortexResponse = Static<typeof SnowflakeCortexResponse>;

// Conformer result types
export const SnowflakeCortexConformerResult = RRecord({
  messages: Array(SnowflakeCortexMessage),
  totalContextTokens: Number.Or(Null),
  usedTokenCountApproximation: Boolean,
});
export type SnowflakeCortexConformerResult = Static<
  typeof SnowflakeCortexConformerResult
>;

// Internal response processing types
export const SnowflakeCortexContentItem = RRecord({
  type: Optional(String),
  text: Optional(String),
  tool_use_id: Optional(String),
  name: Optional(String),
  input: Optional(Union(String, Dictionary(Unknown, String))),
  tool_use: Optional(
    RRecord({
      tool_use_id: Optional(String),
      name: Optional(String),
      input: Optional(Union(String, Dictionary(Unknown, String))),
    }),
  ),
  anthropic: Optional(
    RRecord({
      thinking: Optional(
        RRecord({
          thinking: Optional(String),
          signature: Optional(String),
        }),
      ),
    }),
  ),
});
export type SnowflakeCortexContentItem = Static<
  typeof SnowflakeCortexContentItem
>;

export const SnowflakeCortexResponseMessage = RRecord({
  content: Optional(String),
  content_list: Optional(
    Union(SnowflakeCortexContentItem, Array(SnowflakeCortexContentItem)),
  ),
});
export type SnowflakeCortexResponseMessage = Static<
  typeof SnowflakeCortexResponseMessage
>;

export const SnowflakeCortexResponseDelta = RRecord({
  content: Optional(String),
  text: Optional(String),
  content_list: Optional(
    Union(SnowflakeCortexContentItem, Array(SnowflakeCortexContentItem)),
  ),
});
export type SnowflakeCortexResponseDelta = Static<
  typeof SnowflakeCortexResponseDelta
>;

export const SnowflakeCortexResponseItemChoice = RRecord({
  message: Optional(SnowflakeCortexResponseMessage),
  delta: Optional(SnowflakeCortexResponseDelta),
  finish_reason: Optional(String),
});
export type SnowflakeCortexResponseItemChoice = Static<
  typeof SnowflakeCortexResponseItemChoice
>;

export const SnowflakeCortexResponseItemUsage = RRecord({
  total_tokens: Optional(Number),
});
export type SnowflakeCortexResponseItemUsage = Static<
  typeof SnowflakeCortexResponseItemUsage
>;

export const SnowflakeCortexResponseItem = RRecord({
  id: Optional(String),
  model: Optional(String),
  choices: Array(SnowflakeCortexResponseItemChoice),
  usage: Optional(SnowflakeCortexResponseItemUsage),
});
export type SnowflakeCortexResponseItem = Static<
  typeof SnowflakeCortexResponseItem
>;

// Error types
export const SnowflakeCortexErrorPayload = RRecord({
  type: Literal("error"),
  error: RRecord({
    type: String,
    message: String,
  }),
});
export type SnowflakeCortexErrorPayload = Static<
  typeof SnowflakeCortexErrorPayload
>;
